import PropTypes from 'prop-types';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { cloneDeep } from 'lodash';
import { Button, Col } from 'react-bootstrap';
import { StandaloneProduct } from '@upstart/harmony/lib/legos/websiteTypes';
import Switch from 'components/Inputs/Switch';
import CodeTextArea from 'components/WidgetConfigEditor/inputs/CodeTextArea';
import websiteActions from '../../../redux/websites/actions';

import '../Website.scss';

interface HTMLInputElementCheckbox extends Omit<HTMLInputElement, 'value'> {
  value: boolean;
}

const PURCHASE_TYPE_SELECTION_NAME = 'Purchase type selection';
const STANDALONE_VEHICLE_SELECTION_NAME = 'Standalone vehicle selection';

function WebsiteStandaloneProducts(props: any) {
  const { actions, history, website } = props;

  const [formData, setFormData] = useState({});
  // Initialize purchaseType from website config, fallback to true if not set
  const [purchaseType, setPurchaseType] = useState<boolean>(
    website?.configs?.standalonePurchaseType === true,
  );
  // inverted: disable flag false/undefined → switch shows ON (enabled by default)
  const [standaloneVehicleSelection, setStandaloneVehicleSelection] =
    useState<boolean>(!website?.configs?.standaloneVehicleSelectionDisabled);
  const [allProducts] = useState(Object.values(StandaloneProduct));
  const [selectedProducts, setSelectedProducts] = useState<StandaloneProduct[]>(
    [],
  );

  useEffect(() => {
    setFormData({
      ...allProducts.reduce(
        (acc, product) =>
          product === StandaloneProduct.CREDIT_APPLICATION
            ? {
                ...acc,
                [product]: !!selectedProducts.includes(product),
                [PURCHASE_TYPE_SELECTION_NAME]: purchaseType,
                [STANDALONE_VEHICLE_SELECTION_NAME]: standaloneVehicleSelection,
              }
            : {
                ...acc,
                [product]: !!selectedProducts.includes(product),
              },
        {},
      ),
    });
  }, [selectedProducts]);

  useEffect(() => {
    setSelectedProducts(website?.configs?.standaloneProducts ?? []);
    // sync purchaseType when website data changes
    setPurchaseType(website?.configs?.standalonePurchaseType === true);
    setStandaloneVehicleSelection(
      !website?.configs?.standaloneVehicleSelectionDisabled,
    );
  }, [website]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value: selected } = e.target;
    if (selected) {
      setSelectedProducts([...selectedProducts, name as StandaloneProduct]);
    } else {
      setSelectedProducts(
        selectedProducts.filter((product) => product !== name),
      );
    }
  };

  const switchPurchaseType = (e: ChangeEvent<HTMLInputElementCheckbox>) => {
    const { name, value } = e.target;
    setPurchaseType(value);
    setFormData({
      ...allProducts.reduce(
        (acc, product) =>
          product === StandaloneProduct.CREDIT_APPLICATION
            ? {
                ...acc,
                [product]: !!selectedProducts.includes(product),
                [STANDALONE_VEHICLE_SELECTION_NAME]: standaloneVehicleSelection,
                [name]: value,
              }
            : {
                ...acc,
                [product]: !!selectedProducts.includes(product),
              },
        {},
      ),
    });
  };

  const switchStandaloneVehicleSelection = (
    e: ChangeEvent<HTMLInputElementCheckbox>,
  ) => {
    const { name, value } = e.target;
    setStandaloneVehicleSelection(value);
    setFormData({
      ...allProducts.reduce(
        (acc, product) =>
          product === StandaloneProduct.CREDIT_APPLICATION
            ? {
                ...acc,
                [product]: !!selectedProducts.includes(product),
                [PURCHASE_TYPE_SELECTION_NAME]: purchaseType,
                [name]: value,
              }
            : {
                ...acc,
                [product]: !!selectedProducts.includes(product),
              },
        {},
      ),
    });
  };

  const renderProduct = (product: StandaloneProduct) => (
    <Col sm={6} className="product" key={product}>
      <h4>Standalone {product}</h4>
      <Switch
        formData={formData}
        name={product}
        label={product.toLowerCase()}
        onChange={handleChange}
      />
      {!!selectedProducts.includes(product) && (
        <>
          {product === StandaloneProduct.CREDIT_APPLICATION && (
            <>
              <Switch
                formData={formData}
                name={PURCHASE_TYPE_SELECTION_NAME}
                label={PURCHASE_TYPE_SELECTION_NAME.toLowerCase()}
                onChange={switchPurchaseType}
              />
              <Switch
                formData={formData}
                name={STANDALONE_VEHICLE_SELECTION_NAME}
                label={STANDALONE_VEHICLE_SELECTION_NAME.toLowerCase()}
                onChange={switchStandaloneVehicleSelection}
              />
            </>
          )}
          <CodeTextArea
            label="Container"
            value={`<!— Copy this markup into your page template where you want ${product} to be rendered —>\n<div class="uar-standalone-product-${product
              .toLowerCase()
              .split(/\s+/)
              .join('-')}"></div>`}
          />
        </>
      )}
    </Col>
  );

  const onSave = async () => {
    const newWebsite = cloneDeep(website);
    newWebsite.configs = newWebsite.configs ?? {};
    newWebsite.configs.standaloneProducts = selectedProducts;
    newWebsite.configs.standalonePurchaseType = purchaseType;
    newWebsite.configs.standaloneVehicleSelectionDisabled =
      !standaloneVehicleSelection;
    actions.updateWebsiteRequest({ website: newWebsite });
  };

  const cancel = () => {
    history.push('/websites/');
  };

  return (
    <div className="website-tab-container standalone-products">
      <div className="products">{allProducts.map(renderProduct)}</div>
      <div className="button-container">
        <Button className="btn btn-secondary" type="button" onClick={cancel}>
          Cancel
        </Button>
        <Button className="btn btn-primary" type="button" onClick={onSave}>
          Save
        </Button>
      </div>
    </div>
  );
}

WebsiteStandaloneProducts.propTypes = {
  website: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
};

const mapStateToProps = (state: any) => ({
  website: state.websites.website,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    {
      ...websiteActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WebsiteStandaloneProducts);
